import authService from 'src/services/auth-service';
import loginStorageService from 'src/app/login/services/login-storage-service';
import { resetCustomers } from 'src/app/customers/state/customersSlice';
import { resetProjects } from 'src/app/projects/views/project-list/state/projectListSlice';
import { resetRoutes } from 'src/routes/state/routesSlice';
import { useAppDispatch } from 'src/store';
import { useEffect, useState } from 'react';
import useAppNavigation from './useAppNavigation';
import authStorageService from './services/auth-storage-service';
import { UserGroup, UserPermissions } from '../types';

type CustomAuthDataRefreshedEvent = {
    accessToken: string;
    accessTokenExpiry: string;
};

const useAuthService = () => {
    const navigate = useAppNavigation();
    const dispatch = useAppDispatch();

    const [accessTokenData, setAccessTokenData] = useState({
        accessToken: authStorageService.getAccessToken(),
        accessTokenExpiry: authStorageService.getAccessTokenExpiry(),
    });

    const [permissions, setPermissions] = useState<UserPermissions | null>(authStorageService.getPermissions());

    const handleAuthDataRefreshedCustomEvent = (event: CustomEvent<CustomAuthDataRefreshedEvent>) => {
        setAccessTokenData({
            accessToken: event.detail.accessToken,
            accessTokenExpiry: event.detail.accessTokenExpiry,
        });
    };

    const handlePermissionsRefreshedEvent = (event: CustomEvent<UserPermissions>) => {
        setPermissions(event.detail);
    };

    useEffect(() => {
        window.addEventListener('auth_data_refreshed', handleAuthDataRefreshedCustomEvent as EventListener);
        window.addEventListener('permissions_refreshed', handlePermissionsRefreshedEvent as EventListener);
        return () => {
            window.removeEventListener('auth_data_refreshed', handleAuthDataRefreshedCustomEvent as EventListener);
            window.removeEventListener('permissions_refreshed', handlePermissionsRefreshedEvent as EventListener);
        };
    }, []);

    const isCurrentUserAuthenticated = (): boolean => {
        const accessToken = accessTokenData.accessToken;
        const accessTokenExpiry = accessTokenData.accessTokenExpiry;
        const hasGivenTncConsent = loginStorageService.getTermsOfUseConsent();

        // if it is refreshing, return true
        // authService will automaticallt logout the user if the refresh fails
        if (authService.isRefreshing) {
            return true;
        }

        // Conside the user un-authenticated unless the tnc consent is given
        if (!hasGivenTncConsent) {
            return false;
        }

        // The user is authenticated and the session is valid
        if (accessToken && accessTokenExpiry && new Date(accessTokenExpiry) > new Date()) {
            return true;
        }

        return false;
    };

    const redirectIfNotAuthenticated = () => {
        if (!isCurrentUserAuthenticated()) {
            navigate.toHome();
        }
    };

    const getCurrentUserData = () => {
        return authService.getCurrentUserData();
    };

    const isCurrentUserSuperAdmin = (): boolean => {
        const currentUserData = getCurrentUserData();

        if (!currentUserData) {
            return false;
        }

        return Boolean(currentUserData?.groups && currentUserData?.groups.includes(UserGroup.SUPER_ADMIN));
    };

    const signout = () => {
        authService.signout(() => {
            dispatch(resetCustomers());
            dispatch(resetProjects());
            dispatch(resetRoutes());
        });
    };

    return {
        currentUser: getCurrentUserData(),
        isAuthenticated: isCurrentUserAuthenticated(),
        isCurrentUserSuperAdmin: isCurrentUserSuperAdmin(),
        hasGivenTncConsent: loginStorageService.getTermsOfUseConsent() || false,
        permissions,
        redirectIfNotAuthenticated,
        signout,
    };
};

export default useAuthService;
